import { useLayoutEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { WidgetBackground } from '@elfsight/widget-ui';
import { getRGBColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { meta } from '../meta';
import {
  Button,
  Deactivation,
  FreeLink,
  Header,
  Layout,
  Logo
} from './components';
import { Color, Solver } from './helpers';
import { useLogos } from './logo';
import { useThumbnailMode } from './utils';

const MIN_RESIZE_DELTA = 30;
const MIN_PADDING = 40;

const getPaddingResult = ({ padding, thumbnailMode, color }) => {
  if (!thumbnailMode) {
    return padding;
  }

  if (padding < MIN_PADDING || color === 'transparent') {
    return MIN_PADDING;
  }

  return padding;
};

const getContentStyles = ({ gutter, widgetTitle, buttonEnabled }) => {
  const calculateGutter = (gutter) => Math.max(gutter / 2, MIN_PADDING);

  const marginTop = widgetTitle ? calculateGutter(gutter) : 0;
  const marginBottom = buttonEnabled ? calculateGutter(gutter) : 0;

  return {
    marginTop,
    marginRight: 0,
    marginBottom,
    marginLeft: 0
  };
};

export function Widget() {
  const {
    logoColor,
    logoCustomColor,
    gutter,
    widgetBackground,
    widgetVerticalPadding,
    widgetHorizontalPadding,
    widgetTitle,
    buttonEnabled
  } = useSettings();

  const [{ filter }, setColor] = useState({});
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [measureRef] = useRect(
    ({ width }) => {
      const isDeltaWidthSufficient =
        Math.abs(layoutWidth - width) > MIN_RESIZE_DELTA;

      if (isDeltaWidthSufficient) {
        setLayoutWidth(width);
      }
    },
    {
      resize: true
    }
  );

  const { thumbnailMode, thumbnailModeBackground, thumbnailModeStyle } =
    useThumbnailMode();
  const logos = useLogos();

  useLayoutEffect(() => {
    if (logoColor === 'custom' && logoCustomColor) {
      const rgbColor = getRGBColor(logoCustomColor);
      const rgb = rgbColor
        .substring(4, rgbColor.length - 1)
        .replace(/ /g, '')
        .split(',');

      const colorItem = new Color(...rgb);
      const solver = new Solver(colorItem);

      setColor(solver.solve());
    }
  }, [logoColor, logoCustomColor]);

  const contentStyles = getContentStyles({
    gutter,
    widgetTitle,
    buttonEnabled
  });

  const logoColorStyle = {
    original: '',
    grayscale: 'filter: grayscale(1);',
    custom: `filter: ${filter};`
  }[logoColor];

  return (
    <Deactivation>
      <WidgetBackground
        {...(thumbnailMode ? thumbnailModeBackground : widgetBackground)}
        horizontalPadding={getPaddingResult({
          padding: widgetHorizontalPadding,
          color: widgetBackground.color,
          thumbnailMode
        })}
        verticalPadding={getPaddingResult({
          padding: widgetVerticalPadding,
          color: widgetBackground.color,
          thumbnailMode
        })}
        style={thumbnailMode ? thumbnailModeStyle : undefined}
      >
        <Component ref={measureRef} _logoFilter={logoColorStyle}>
          <Header />

          <Content style={contentStyles}>
            <Show when={!!logos.length}>
              <Layout width={layoutWidth}>
                {logos.map((item, index) => (
                  <Logo
                    width={layoutWidth}
                    data={item}
                    key={item.id || index}
                  />
                ))}
              </Layout>
            </Show>
          </Content>

          <Button />
          <FreeLink />
        </Component>
      </WidgetBackground>
    </Deactivation>
  );
}

const Component = styled.div.attrs({
  className: `${meta.prefix}-logo-showcase-component`
})`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ _logoFilter }) => css`
    & .${meta.prefix}-logo-inner {
      ${_logoFilter};
    }
  `};
`;

const Content = styled.div.attrs({
  className: `${meta.prefix}-logo-showcase-content`
})`
  display: flex;
  width: 100%;
  padding: 0;
  overflow: hidden;
`;
